import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Archive, CircleCheckBig } from "lucide-react";
import { Task } from "./types";
import React from "react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { isTaskOverdue } from "@/lib/task-utils";

interface TaskCardProps {
  task: Task;
  onDelete: (id: string) => void;
  onArchive?: (id: string) => void;
  showArchive?: boolean;
  onTaskClick?: (task: Task) => void;
  onToggleComplete?: (id: string, completed: boolean) => void;
  enableUpvotes?: boolean;
  onToggleUpvote?: (id: string) => void;
  currentUserId?: string;
}

const priorityToColor: Record<Task["priority"], string> = {
  low: "#6B7280", // gray
  medium: "#3B82F6", // blue
  high: "#F59E0B", // orange
  critical: "#EF4444", // red
};

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onArchive,
  showArchive,
  onTaskClick,
  onToggleComplete,
  enableUpvotes,
  onToggleUpvote,
  currentUserId,
}) => {
  const priorityColor = priorityToColor[task.priority];
  const isOverdue = isTaskOverdue(task);
  const hasUpvoted = Boolean(currentUserId && task.upvotedBy?.includes(currentUserId));
  const upvoteCount = task.upvotes ?? (task.upvotedBy?.length || 0);

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onTaskClick?.(task);
    }
  };

  const handleToggleComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleComplete?.(task.id, !task.completed);
  };

  const handleUpvote = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleUpvote?.(task.id);
  };

  return (
    <Card
      className={cn(
        "bg-white dark:bg-card border border-border shadow-sm transition-all will-change-transform hover:shadow-md cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-workspace-accent focus-visible:ring-offset-2",
        isOverdue && !task.completed && "ring-2 ring-destructive"
      )}
      onClick={(e) => {
        if (e.defaultPrevented) return;
        onTaskClick?.(task);
      }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`Task: ${task.title}`}
    >
      <CardContent className="p-3">
        <div className={cn("flex items-start gap-2.5", !enableUpvotes && "block")}>
          {/* Upvote Button (Left Tile matching reference design) */}
          {enableUpvotes && (
            <button
              type="button"
              onClick={handleUpvote}
              className={cn(
                "flex flex-col items-center justify-center min-w-[42px] w-[42px] h-[48px] rounded-[14px] border transition-all flex-shrink-0 cursor-pointer active:scale-95 select-none shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
                hasUpvoted
                  ? "bg-workspace-accent/10 border-workspace-accent text-workspace-accent shadow-xs"
                  : "bg-white dark:bg-card border-slate-200 dark:border-slate-700/80 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
              )}
              title={hasUpvoted ? "Remove upvote" : "Upvote task"}
              aria-label={`${upvoteCount} upvotes. ${hasUpvoted ? "Remove upvote" : "Upvote task"}`}
            >
              <svg
                viewBox="0 0 24 22"
                fill={hasUpvoted ? "currentColor" : "none"}
                className={cn(
                  "w-3.5 h-3.5 transition-colors",
                  hasUpvoted ? "text-workspace-accent fill-workspace-accent/20" : "text-slate-600 dark:text-slate-400"
                )}
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 4.5l7.5 13a1.5 1.5 0 0 1-1.3 2.2H5.8a1.5 1.5 0 0 1-1.3-2.2L12 4.5z" />
              </svg>
              <span
                className={cn(
                  "text-[13px] font-bold leading-none mt-1 tabular-nums",
                  hasUpvoted ? "text-workspace-accent" : "text-slate-900 dark:text-slate-100"
                )}
              >
                {upvoteCount}
              </span>
            </button>
          )}

          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: priorityColor }}
                  aria-label={`${task.priority} priority`}
                />
                <h3 className={cn("font-medium text-sm leading-tight truncate", task.completed && "line-through opacity-60")}>
                  {task.title}
                </h3>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={handleToggleComplete}
                  className="transition-all focus:outline-none focus:ring-2 focus:ring-workspace-accent focus:ring-offset-1 rounded-full"
                  title={task.completed ? "Mark as incomplete" : "Mark as complete"}
                  aria-label={task.completed ? "Mark as incomplete" : "Mark as complete"}
                >
                  <CircleCheckBig
                    className={cn(
                      "h-5 w-5 transition-colors",
                      task.completed ? "text-green-500" : "text-muted-foreground/40"
                    )}
                  />
                </button>
                {showArchive && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Archive task"
                    onClick={(e) => {
                      e.stopPropagation();
                      onArchive?.(task.id);
                    }}
                    title="Archive"
                    className="h-6 w-6"
                  >
                    <Archive className="h-3 w-3 opacity-80" />
                  </Button>
                )}
              </div>
            </div>

            {/* Description snippet if present and upvotes enabled */}
            {task.description && (
              <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                {task.description}
              </p>
            )}

            {/* Due Date and Assignees - Horizontal Layout */}
            {(task.dueDate || (task.assignees && task.assignees.length > 0)) && (
              <div className="flex items-center justify-between gap-2 pt-0.5">
                {/* Due Date */}
                {task.dueDate ? (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <span>Due: {format(new Date(task.dueDate), "MMM d")}</span>
                  </div>
                ) : (
                  <div />
                )}

                {/* Assignees */}
                {task.assignees && task.assignees.length > 0 && (
                  <div className="flex items-center gap-1">
                    {task.assignees.map((assignee) => (
                      <Avatar key={assignee.id} className="h-5 w-5">
                        <AvatarImage src={assignee.avatar} alt={assignee.name} />
                        <AvatarFallback className="text-xs">
                          {getInitials(assignee.name)}
                        </AvatarFallback>
                      </Avatar>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
